import { Box, Button, Grid, InlineStack, PanelSection, Stack, TextField } from '@customerio/pluma-components/react';
import { customerDisplay } from '@/services/db/customer/display';
import type { Customer } from '@/services/db/customer/entity';
import {
  ProfileType,
  type MergeConfirmations,
  type MergedAttributes,
  type MergeSummary,
  type MergeWorkspaceContext,
  type SelectableProfileType,
  type SelectedCustomers,
} from '../types';
import { MergeConfirmationsPanel } from './merge-confirmations-panel';
import { MergeSummaryPanel } from './merge-summary-panel';
import { SelectCustomerCard } from './select-customer-card';

/** What every card on either step reads. */
export type MergeCardProps = {
  workspaceId: number;
  selected: SelectedCustomers;
  context: MergeWorkspaceContext;
  searchTerm: string;
  isLoadingMergedAttributes: boolean;
  mergedAttributes: MergedAttributes;
  mergeSummary: MergeSummary | undefined;
};

type SetupStepProps = {
  cardProps: MergeCardProps;
  /** Which card instance sits in which slot, so a swap moves cards instead of re-rendering them. */
  cardKeys: Record<SelectableProfileType, string>;
  onSelect: (type: SelectableProfileType, customer: Customer | null) => void;
};

/** The setup step: the pair of selectable cards, side by side. */
export function SetupStep({ cardProps, cardKeys, onSelect }: SetupStepProps) {
  return (
    <PanelSection>
      <InlineStack alignBlock="stretch" gap="300">
        {/* Keyed by card identity so a swap moves each card (and its search state) across, rather than re-rendering both. */}
        <Box flex={1} key={cardKeys.primary}>
          <SelectCustomerCard
            {...cardProps}
            profileType={ProfileType.Primary}
            onSelect={(customer) => onSelect(ProfileType.Primary, customer)}
          />
        </Box>
        <Box flex={1} key={cardKeys.secondary}>
          <SelectCustomerCard
            {...cardProps}
            profileType={ProfileType.Secondary}
            onSelect={(customer) => onSelect(ProfileType.Secondary, customer)}
          />
        </Box>
      </InlineStack>
    </PanelSection>
  );
}

type ReviewStepProps = {
  cardProps: MergeCardProps;
  confirmations: MergeConfirmations;
  /** The viewer may edit people; a viewer who may not sees the step read-only. */
  canMerge: boolean;
  onChangeConfirmations: (confirmations: MergeConfirmations) => void;
  onRequestFeedback: () => void;
};

/** The review step: the projected merged profile beside the summary and the confirmations. */
export function ReviewStep({
  cardProps,
  confirmations,
  canMerge,
  onChangeConfirmations,
  onRequestFeedback,
}: ReviewStepProps) {
  const { selected, context } = cardProps;
  const secondary = selected.secondary;

  return (
    <PanelSection>
      <Grid columns={2} gap="300">
        <Box>
          <SelectCustomerCard {...cardProps} profileType={ProfileType.Merged} />
        </Box>
        <Stack gap="300">
          <MergeSummaryPanel
            summary={cardProps.mergeSummary}
            secondaryDisplayName={secondary == null ? '' : customerDisplay(secondary, context.terms.peopleSingular)}
            peopleTerm={context.terms.peoplePlural}
            onRequestFeedback={onRequestFeedback}
          />
          <MergeConfirmationsPanel
            confirmations={confirmations}
            canMerge={canMerge}
            campaignTerm={context.terms.campaignsSingular}
            personTerm={context.terms.peopleSingular}
            peopleTerm={context.terms.peoplePlural}
            onChange={onChangeConfirmations}
          />
        </Stack>
      </Grid>
    </PanelSection>
  );
}

type MergeToolbarProps = {
  searchTerm: string;
  /** The search only exists to filter a card's attributes, so it hides while no card has any. */
  isSearchHidden: boolean;
  /** Swap only makes sense between the two selectable cards, so it appears on the setup step alone. */
  isSwapShown: boolean;
  isSwapDisabled: boolean;
  onSearchTermChange: (term: string) => void;
  onSwap: () => void;
};

/** The panel's top row: the attribute-name filter, and Swap on the setup step. */
export function MergeToolbar({
  searchTerm,
  isSearchHidden,
  isSwapShown,
  isSwapDisabled,
  onSearchTermChange,
  onSwap,
}: MergeToolbarProps) {
  return (
    <PanelSection>
      <Grid columns={2} alignItems="center" gap="300">
        <Box visibility={isSearchHidden ? 'hidden' : 'visible'}>
          <TextField
            ariaLabel="search-attribute"
            placeholder="Search attribute name…"
            icon="search"
            value={searchTerm}
            onInput={(event) => onSearchTermChange(event.target.value)}
          />
        </Box>
        <Box display="flex" justifyContent="flex-end">
          {isSwapShown ? (
            <Button
              variant="tertiary"
              icon="swap"
              isDisabled={isSwapDisabled}
              onClick={onSwap}
              data-test="people-merge-swap"
            >
              Swap
            </Button>
          ) : null}
        </Box>
      </Grid>
    </PanelSection>
  );
}
